import { useState, useEffect, useMemo, useRef } from 'react'
import { Sparkles, Building2, Anchor, TrendingUp, Calculator, BarChart3 } from 'lucide-react'
import { matchingApi } from '../../services/api'

const CLIENT_QUALITY_OPTIONS = [
  { value: 'high', label: 'High (Fortune 2000 / US-EU)', adjustment: '+10%' },
  { value: 'medium', label: 'Medium (Mid-size clients)', adjustment: '0%' },
  { value: 'low', label: 'Low (Startups)', adjustment: '-10%' }
]

const PARTNERSHIP_TIER_OPTIONS = [
  { value: 'elite', label: 'Elite', adjustment: '+15%' },
  { value: 'basic', label: 'Basic', adjustment: '+5%' },
  { value: 'none', label: 'None', adjustment: '0%' }
]

const DEFAULTS = {
  clientQuality: 'medium',
  partnershipTier: 'basic',
  smCostPct: 8
}

const formatCurrency = (value) => {
  if (!value && value !== 0) return 'N/A'
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}B`
  if (value < 1) return `$${(value * 1000).toFixed(0)}k`
  return `$${value.toFixed(1)}M`
}

export default function ValuationIntelligence({ target, onValuationChange }) {
  const [revenue, setRevenue] = useState(target.revenue || '')
  const [ebitdaPct, setEbitdaPct] = useState(target.ebitdaPct || '')
  const [clientQuality, setClientQuality] = useState(DEFAULTS.clientQuality)
  const [partnershipTier, setPartnershipTier] = useState(DEFAULTS.partnershipTier)
  const [smCostPct, setSmCostPct] = useState(DEFAULTS.smCostPct)

  const [sectorMedian, setSectorMedian] = useState(null)
  const [medianEvEbitda, setMedianEvEbitda] = useState(null)
  const [ebitdaAnalysis, setEbitdaAnalysis] = useState(null)
  const [ebitdaAdjustment, setEbitdaAdjustment] = useState(0)
  const [matchedDeals, setMatchedDeals] = useState([])
  const [matchReasoning, setMatchReasoning] = useState('')
  const [totalDealsWithEvRevenue, setTotalDealsWithEvRevenue] = useState(0)
  const [totalDealsWithEvEbitda, setTotalDealsWithEvEbitda] = useState(0)
  const [totalMatches, setTotalMatches] = useState(0)
  const [keyCapabilities, setKeyCapabilities] = useState([])
  const [loading, setLoading] = useState(true)

  // Track loaded target to prevent reloading on tab switch
  const loadedTargetRef = useRef(null)

  useEffect(() => {
    // Only load if target changed
    if (loadedTargetRef.current !== target.id) {
      loadData()
    }
  }, [target.id])

  const loadData = async () => {
    setLoading(true)
    try {
      const dealsResponse = await matchingApi.findDeals({
        sector: target.sector,
        capabilities: target.capabilities,
        industries: target.industries || [],
        targetId: target.id,
        ebitdaMarginPct: ebitdaPct,
        revenueMM: revenue,
        // Pass expertCapabilities for tiered matching (first 3, then expand to 6 if needed)
        expertCapabilities: target.expertCapabilities || []
      })

      setMatchedDeals(dealsResponse.deals || [])
      setMatchReasoning(dealsResponse.reasoning || '')
      setSectorMedian(dealsResponse.medianEvRevenue || 2.0)
      setMedianEvEbitda(dealsResponse.medianEvEbitda || null)
      setEbitdaAnalysis(dealsResponse.ebitdaAnalysis || null)
      setEbitdaAdjustment(dealsResponse.ebitdaAdjustment || 0)
      setTotalDealsWithEvRevenue(dealsResponse.dealsWithEvRevenue || 0)
      setTotalDealsWithEvEbitda(dealsResponse.dealsWithEvEbitda || 0)
      setTotalMatches(dealsResponse.totalMatches || 0)
      // Store top 3 capabilities used for matching as Key Capabilities
      const capsUsed = dealsResponse.capabilitiesUsed || target.expertCapabilities || target.capabilities || []
      setKeyCapabilities(capsUsed.slice(0, 3).map(c => typeof c === 'string' ? c : c.name))
      loadedTargetRef.current = target.id
    } catch (error) {
      console.error('Failed to load data:', error)
      setSectorMedian(2.0)
    } finally {
      setLoading(false)
    }
  }

  const valuation = useMemo(() => {
    // === 1. ANCHOR VALUATION (EV/Revenue) ===
    const evRevenueMultiple = sectorMedian || 2.0
    const anchorValuation = revenue * evRevenueMultiple

    // === 2. GROWTHPAL SUGGESTION (with all adjusters) ===
    const clientAdj = { high: 1.10, medium: 1.0, low: 0.90 }[clientQuality]
    const partnerAdj = { elite: 1.15, basic: 1.05, none: 1.0 }[partnershipTier]
    const smAdj = smCostPct >= 10 ? 1.0 : 1 - Math.min(0.10, (10 - smCostPct) / 100)

    // EBITDA adjustment from database table (based on revenue range and EBITDA margin)
    const ebitdaAdj = 1 + (ebitdaAdjustment || 0)

    // Total adjustment factor
    const totalAdjFactor = clientAdj * partnerAdj * smAdj * ebitdaAdj

    // Use anchor valuation as base for GrowthPal suggestion
    const gpBase = anchorValuation * totalAdjFactor
    const gpLow = gpBase * 0.90
    const gpHigh = gpBase * 1.10

    return {
      // Anchor valuation (EV/Revenue)
      anchorEvRevenue: evRevenueMultiple,
      anchorValuation,

      // GrowthPal suggestion (range)
      gpLow,
      gpBase,
      gpHigh,

      // Legacy: keep 'predicted' for ROI compatibility
      predicted: gpBase,
      evRevenueMultiple,
      revenue,
      ebitdaPct,

      // Individual adjustments
      adjustments: {
        client: (clientAdj - 1) * 100,
        partner: (partnerAdj - 1) * 100,
        sm: (smAdj - 1) * 100,
        ebitda: ebitdaAdjustment * 100
      }
    }
  }, [revenue, sectorMedian, clientQuality, partnershipTier, smCostPct, ebitdaPct, ebitdaAdjustment])

  // Notify parent of valuation changes
  useEffect(() => {
    if (onValuationChange && !loading) {
      onValuationChange(valuation)
    }
  }, [valuation, loading, onValuationChange])

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6 animate-pulse">
        <div className="h-6 bg-neutral-200 rounded w-48 mb-4" />
        <div className="h-32 bg-neutral-200 rounded" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* User Details Input Section */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h2 className="text-base font-semibold text-neutral-900 mb-1">Please enter your details to unlock valuation insights</h2>
        <p className="text-xs text-neutral-500 mb-4">Fill in the required fields below to generate your valuation analysis</p>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm text-neutral-600 mb-2">
              Revenue ($ Million) <span className="text-red-500">*</span>
            </label>
            <input
              id="revenue-input"
              type="number"
              value={revenue}
              onChange={(e) => setRevenue(parseFloat(e.target.value) || '')}
              placeholder="Enter revenue"
              className={`w-full h-11 px-4 border rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none ${!revenue ? 'border-red-300 bg-red-50' : 'border-neutral-200'}`}
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-600 mb-2">
              EBITDA Margin (%) <span className="text-red-500">*</span>
            </label>
            <input
              id="ebitda-input"
              type="number"
              value={ebitdaPct}
              onChange={(e) => setEbitdaPct(parseFloat(e.target.value) || '')}
              placeholder="Enter EBITDA margin"
              className={`w-full h-11 px-4 border rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none ${!ebitdaPct ? 'border-red-300 bg-red-50' : 'border-neutral-200'}`}
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-600 mb-2">Client Quality</label>
            <select
              value={clientQuality}
              onChange={(e) => setClientQuality(e.target.value)}
              className="w-full h-11 px-4 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none appearance-none bg-white"
            >
              {CLIENT_QUALITY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.adjustment})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-neutral-600 mb-2">Partnership Tier</label>
            <select
              value={partnershipTier}
              onChange={(e) => setPartnershipTier(e.target.value)}
              className="w-full h-11 px-4 border border-neutral-200 rounded-lg text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none appearance-none bg-white"
            >
              {PARTNERSHIP_TIER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} ({opt.adjustment})
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm text-neutral-600 mb-2">
              Sales & Marketing Cost: {smCostPct}% of Revenue
            </label>
            <input
              type="range"
              min="0"
              max="20"
              value={smCostPct}
              onChange={(e) => setSmCostPct(parseInt(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-neutral-400 mt-1">
              <span>0%</span>
              <span>10% (baseline)</span>
              <span>20%</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-neutral-400 mt-4">
          <span className="text-red-500">*</span> Required fields. These inputs drive valuation calculations below.
        </p>
      </div>

      {/* Valuation Methods Section */}
      <div className="relative">
        {/* Blur overlay when required fields not filled */}
        {(!revenue || !ebitdaPct) && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
            <p className="text-neutral-500 font-medium">Enter <span className="underline cursor-pointer hover:text-primary-600" onClick={() => document.getElementById('revenue-input')?.focus()}>Revenue</span> and <span className="underline cursor-pointer hover:text-primary-600" onClick={() => document.getElementById('ebitda-input')?.focus()}>EBITDA Margin</span> to unlock Valuation Intelligence</p>
          </div>
        )}

        <h2 className="text-base font-semibold text-neutral-900 mb-1">Valuation Intelligence</h2>
        <p className="text-xs text-neutral-500 mb-4">GrowthPal calculates four different valuations based on different methods</p>

        <div className="grid grid-cols-3 gap-4 mb-4">
          {/* 1. Anchor Valuation (EV/Revenue) */}
          <div className="bg-white rounded-lg border border-blue-200 p-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center">1</span>
              <h3 className="text-sm font-semibold text-neutral-900">Anchor Valuation</h3>
            </div>
            <p className="text-xs text-neutral-500 mb-3 ml-8">Revenue-based valuation using median EV/Revenue multiple from comparable M&A transactions</p>

          <div className="bg-blue-50 rounded-lg p-4 mb-3">
            <p className="text-xs text-blue-600 mb-1">EV/Revenue Multiple (Median)</p>
            <p className="text-3xl font-bold text-blue-700">{valuation.anchorEvRevenue.toFixed(2)}x</p>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Revenue:</span>
              <span className="font-medium text-neutral-900">{formatCurrency(revenue)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-neutral-100">
              <span className="text-neutral-700 font-medium">Calculated Valuation:</span>
              <span className="font-semibold text-blue-700">{formatCurrency(valuation.anchorValuation)}</span>
            </div>
          </div>

          <p className="text-xs text-neutral-400 mt-3">
            Based on median EV/Revenue across {totalDealsWithEvRevenue >= 5 ? totalDealsWithEvRevenue : 'a few'} comparable deals
          </p>
        </div>

        {/* 2. EBITDA Valuation */}
        <div className="bg-white rounded-lg border border-emerald-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center justify-center">2</span>
            <h3 className="text-sm font-semibold text-neutral-900">EBITDA Valuation</h3>
          </div>
          <p className="text-xs text-neutral-500 mb-3 ml-8">Profitability-based valuation using median EV/EBITDA multiple from comparable deals</p>

          <div className="bg-emerald-50 rounded-lg p-4 mb-3">
            <p className="text-xs text-emerald-600 mb-1">EV/EBITDA Multiple (Median)</p>
            <p className="text-3xl font-bold text-emerald-700">
              {medianEvEbitda ? `${medianEvEbitda.toFixed(1)}x` : 'N/A'}
            </p>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">EBITDA ({ebitdaPct}% margin):</span>
              <span className="font-medium text-neutral-900">{formatCurrency(revenue * ebitdaPct / 100)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-neutral-100">
              <span className="text-neutral-700 font-medium">Calculated Valuation:</span>
              <span className="font-semibold text-emerald-700">
                {medianEvEbitda ? formatCurrency(revenue * ebitdaPct / 100 * medianEvEbitda) : 'N/A'}
              </span>
            </div>
          </div>

          <p className="text-xs text-neutral-400 mt-3">
            Based on median EV/EBITDA across {totalDealsWithEvEbitda >= 5 ? totalDealsWithEvEbitda : 'a few'} comparable deals
          </p>
        </div>

        {/* 3. GrowthPal Suggestion */}
        <div className="bg-white rounded-lg border border-primary-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-bold flex items-center justify-center">3</span>
            <h3 className="text-sm font-semibold text-neutral-900">GrowthPal Suggestion</h3>
          </div>
          <p className="text-xs text-neutral-500 mb-3 ml-8">Anchor valuation adjusted with GrowthPal intelligence factors</p>

          {/* Suggested Range */}
          <div className="bg-primary-50 rounded-lg p-4 mb-3">
            <p className="text-xs text-primary-600 mb-2">Suggested Valuation Range</p>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold text-primary-700">{formatCurrency(valuation.gpLow)}</span>
              <span className="text-neutral-400">–</span>
              <span className="text-lg font-semibold text-primary-700">{formatCurrency(valuation.gpHigh)}</span>
            </div>
            <p className="text-sm font-medium text-primary-600 mt-2">Mid-point: {formatCurrency(valuation.gpBase)}</p>
          </div>

          {/* Adjustments Applied */}
          <div className="space-y-1.5 text-xs">
            <p className="font-medium text-neutral-700 mb-2">Adjustments Applied:</p>
            <div className="flex justify-between">
              <span className="text-neutral-500">Client Quality:</span>
              <span className={valuation.adjustments.client >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {valuation.adjustments.client >= 0 ? '+' : ''}{valuation.adjustments.client.toFixed(0)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Partnership:</span>
              <span className={valuation.adjustments.partner >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {valuation.adjustments.partner >= 0 ? '+' : ''}{valuation.adjustments.partner.toFixed(0)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">S&M Cost:</span>
              <span className={valuation.adjustments.sm >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {valuation.adjustments.sm >= 0 ? '+' : ''}{valuation.adjustments.sm.toFixed(0)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">EBITDA Margin:</span>
              <span className={valuation.adjustments.ebitda >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                {valuation.adjustments.ebitda >= 0 ? '+' : ''}{valuation.adjustments.ebitda.toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
      </div>

        {/* 4. Current Market Trends - Full Width */}
        <div className="bg-white rounded-lg border border-amber-200 p-5">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center">4</span>
            <h3 className="text-sm font-semibold text-neutral-900">Current Market Trends</h3>
          </div>
          <p className="text-xs text-neutral-500 mb-3 ml-8">Real-time market demand and sector activity indicators</p>
          <div className="flex items-center justify-center py-4 text-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-400" />
              </div>
              <div className="text-left">
                <p className="text-sm text-neutral-500">Market demand scores</p>
                <p className="text-xs text-neutral-400">Coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Matched deals */}
      <div className="relative">
        {/* Blur overlay when required fields not filled */}
        {(!revenue || !ebitdaPct) && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
            <p className="text-neutral-500 font-medium">Enter <span className="underline cursor-pointer hover:text-primary-600" onClick={() => document.getElementById('revenue-input')?.focus()}>Revenue</span> and <span className="underline cursor-pointer hover:text-primary-600" onClick={() => document.getElementById('ebitda-input')?.focus()}>EBITDA Margin</span> to unlock Valuation Intelligence</p>
          </div>
        )}
        <div className="bg-white rounded-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary-500" />
              <h2 className="text-base font-semibold text-neutral-900">Comparable Deals</h2>
            </div>
          </div>
          <p className="text-xs text-neutral-500 mb-4">{totalMatches >= 5 ? `${totalMatches} deals found` : 'A few deals found'}, showing top 10 matches</p>

        {/* Key Offerings used for matching */}
        {keyCapabilities.length > 0 && (
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Key Offerings:</span>
            <div className="flex flex-wrap gap-2">
              {keyCapabilities.map((cap, idx) => (
                <span key={idx} className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md">
                  {cap}
                </span>
              ))}
            </div>
          </div>
        )}

        {matchReasoning && (
          <div className="bg-neutral-50 rounded-lg p-4 mb-5">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">Why these deals?</p>
            <p className="text-sm text-neutral-700 leading-relaxed">{matchReasoning}</p>
          </div>
        )}

        <div className="space-y-3">
          {matchedDeals.length > 0 ? (
            matchedDeals.slice(0, 10).map((deal) => (
              <div key={deal.id} className="border border-neutral-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-neutral-900">{deal.targetName}</h4>
                      {deal.matchScore && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          deal.matchScore >= 80 ? 'bg-emerald-100 text-emerald-700' :
                          deal.matchScore >= 60 ? 'bg-primary-100 text-primary-700' :
                          'bg-neutral-100 text-neutral-600'
                        }`}>
                          {deal.matchScore >= 80 ? 'Strong' : deal.matchScore >= 60 ? 'Good' : 'Fair'} match
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500 mt-0.5">Acquired by {deal.acquirer}</p>
                    {deal.headquarters && (
                      <p className="text-xs text-neutral-400 mt-1">{deal.headquarters}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {deal.dealValue && (
                      <p className="font-semibold text-neutral-900">{formatCurrency(deal.dealValue)}</p>
                    )}
                    <div className="flex items-center gap-3 mt-1">
                      {deal.evRevenue && (
                        <div>
                          <p className="text-sm text-blue-600 font-medium">{deal.evRevenue.toFixed(2)}x</p>
                          <p className="text-xs text-neutral-400">EV/Rev</p>
                        </div>
                      )}
                      {deal.evEbitda && (
                        <div className="pl-3 border-l border-neutral-200">
                          <p className="text-sm text-emerald-600 font-medium">{deal.evEbitda.toFixed(1)}x</p>
                          <p className="text-xs text-neutral-400">EV/EBITDA</p>
                        </div>
                      )}
                      {!deal.evRevenue && !deal.evEbitda && (
                        <p className="text-xs text-neutral-400">No multiples</p>
                      )}
                    </div>
                  </div>
                </div>
                {(deal.tag || (deal.capabilities && deal.capabilities.length > 0)) && (
                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-neutral-100">
                    {deal.tag && (
                      <span className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-md">
                        {deal.tag}
                      </span>
                    )}
                    {deal.capabilities?.slice(0, 3).map((cap) => (
                      <span key={cap} className="px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-md">
                        {cap}
                      </span>
                    ))}
                    {deal.capabilities && deal.capabilities.length > 3 && (
                      <span className="text-xs text-neutral-400">+{deal.capabilities.length - 3} more</span>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No comparable deals found</p>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  )
}
